/**
 * MonthNavigator - Componente premium de navegação rápida por mês
 *
 * Visual estilo SaaS enterprise com container estilizado, ícone decorativo,
 * transições suaves e layout refinado.
 *
 * v2.1.0 (aditivo): quando as props opcionais `onPresetChange`/`onCustomChange`
 * são fornecidas, o ícone de calendário decorativo vira um gatilho que abre um
 * popover com presets de período (Hoje / Ontem / Últimos 7/15 dias / Mês
 * Atual / Mês Passado / Personalizado), reaproveitando o mesmo padrão do
 * `CrmPeriodFilter` da página Agenda. Sem essas props, comportamento idêntico
 * ao anterior.
 *
 * @author Sistema
 * @version 2.1.0
 * RELEASE: 2026-07-24 v2.1.0 - Ícone de calendário passa a abrir seletor de
 *          presets de período quando as props opcionais são fornecidas.
 * RELEASE: 2026-04-10 v2.0.0 - Redesign premium do componente
 */

import { useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight, X, CalendarIcon } from 'lucide-react';
import { format, addMonths, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import type { PeriodPreset } from '@/hooks/useCrmOverviewFilters';

/** Opções de presets — mesmas do CrmPeriodFilter (Agenda). */
const PERIOD_OPTIONS: { value: PeriodPreset; label: string }[] = [
  { value: 'today', label: 'Hoje' },
  { value: 'yesterday', label: 'Ontem' },
  { value: '7days', label: 'Últimos 7 dias' },
  { value: '15days', label: 'Últimos 15 dias' },
  { value: 'month', label: 'Mês Atual' },
  { value: 'lastMonth', label: 'Mês Passado' },
];

/**
 * Props do MonthNavigator
 */
interface MonthNavigatorProps {
  /** Mês/ano selecionado atualmente */
  currentMonth: Date;
  /** Callback ao navegar para outro mês */
  onChange: (date: Date) => void;
  /** Limpar filtro de mês (ver todos os meses) */
  onClear: () => void;
  /** Classes CSS adicionais */
  className?: string;
  // ---------------------- Extensão v2.1.0 (opcional) ----------------------
  /**
   * Preset de período ativo. Quando diferente de `null`/`'month'`, oculta as
   * setas de navegação mensal e exibe o label do preset no lugar do mês.
   */
  activePreset?: PeriodPreset | null;
  /**
   * Range customizado ativo (usado quando `activePreset === 'custom'`) —
   * apenas para renderização do label no formato dd/MM/yy - dd/MM/yy.
   */
  customRange?: { startDate: Date; endDate: Date } | null;
  /** Callback disparado ao escolher um preset predefinido no popover. */
  onPresetChange?: (preset: PeriodPreset) => void;
  /** Callback disparado ao confirmar um período personalizado no popover. */
  onCustomChange?: (startDate: Date, endDate: Date) => void;
}

/**
 * Navegador de mês premium com ícone, setas e botão de limpar
 */
export function MonthNavigator({
  currentMonth,
  onChange,
  onClear,
  className,
  activePreset,
  customRange,
  onPresetChange,
  onCustomChange,
}: MonthNavigatorProps) {
  // Estado local do popover de presets — só é usado quando integrado.
  const [presetOpen, setPresetOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customDraft, setCustomDraft] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({
    from: customRange?.startDate,
    to: customRange?.endDate,
  });

  /** Flag: integração de presets ativa (quaisquer callback opcional presente). */
  const presetsEnabled = Boolean(onPresetChange || onCustomChange);

  /**
   * Um preset "não-mensal" está ativo? Nesse caso o navegador se comporta
   * como um chip de exibição do intervalo escolhido (sem setas).
   */
  const nonMonthPresetActive =
    presetsEnabled && activePreset != null && activePreset !== 'month';

  /** Navega para o mês anterior */
  const handlePrev = () => onChange(subMonths(currentMonth, 1));

  /** Navega para o próximo mês */
  const handleNext = () => onChange(addMonths(currentMonth, 1));

  /** Label do mês formatado em pt-BR (ex: "Abril 2026") */
  const monthLabel = format(currentMonth, 'MMMM yyyy', { locale: ptBR });
  const capitalizedMonthLabel = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);

  /** Label a exibir no centro, considerando presets ativos. */
  const centerLabel = (() => {
    if (!nonMonthPresetActive) return capitalizedMonthLabel;
    if (activePreset === 'custom' && customRange) {
      return `${format(customRange.startDate, 'dd/MM/yy')} - ${format(customRange.endDate, 'dd/MM/yy')}`;
    }
    return (
      PERIOD_OPTIONS.find((o) => o.value === activePreset)?.label ??
      capitalizedMonthLabel
    );
  })();

  /** Handler: seleção de preset predefinido no popover. */
  const handlePresetSelect = (preset: PeriodPreset) => {
    if (preset === 'custom') {
      setShowCustom(true);
      return;
    }
    onPresetChange?.(preset);
    setPresetOpen(false);
    setShowCustom(false);
  };

  /** Handler: confirmar range personalizado. */
  const handleCustomConfirm = () => {
    if (customDraft.from && customDraft.to) {
      onCustomChange?.(customDraft.from, customDraft.to);
      setPresetOpen(false);
      setShowCustom(false);
    }
  };

  /** Handler: cancelar personalização. */
  const handleCustomCancel = () => {
    setShowCustom(false);
    setCustomDraft({
      from: customRange?.startDate,
      to: customRange?.endDate,
    });
  };

  // -------------------------------------------------------------------------
  // Ícone de calendário (decorativo ou gatilho de popover)
  // -------------------------------------------------------------------------
  const calendarIconInner = (
    <div
      className={cn(
        'hidden sm:flex items-center justify-center p-1.5 bg-primary/10 ring-1 ring-primary/20 rounded-lg mr-0.5',
        presetsEnabled &&
          'cursor-pointer hover:bg-primary/20 hover:ring-primary/40 transition-colors',
      )}
      title={presetsEnabled ? 'Selecionar período' : undefined}
      aria-label={presetsEnabled ? 'Selecionar período' : undefined}
      role={presetsEnabled ? 'button' : undefined}
    >
      <CalendarDays className="w-3.5 h-3.5 text-primary" />
    </div>
  );

  const calendarIconNode = presetsEnabled ? (
    <Popover
      open={presetOpen}
      onOpenChange={(v) => {
        setPresetOpen(v);
        if (!v) setShowCustom(false);
      }}
    >
      <PopoverTrigger asChild>{calendarIconInner}</PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        {!showCustom ? (
          <Command>
            <CommandList>
              <CommandGroup heading="Período">
                {PERIOD_OPTIONS.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => handlePresetSelect(option.value)}
                    className="cursor-pointer"
                  >
                    <div
                      className={cn(
                        'mr-2 h-4 w-4 rounded-full border',
                        activePreset === option.value
                          ? 'border-primary bg-primary'
                          : 'border-muted',
                      )}
                    >
                      {activePreset === option.value && (
                        <div className="h-full w-full flex items-center justify-center">
                          <div className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />
                        </div>
                      )}
                    </div>
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup>
                <CommandItem
                  value="custom"
                  onSelect={() => handlePresetSelect('custom')}
                  className="cursor-pointer"
                >
                  <div
                    className={cn(
                      'mr-2 h-4 w-4 rounded-full border',
                      activePreset === 'custom'
                        ? 'border-primary bg-primary'
                        : 'border-muted',
                    )}
                  >
                    {activePreset === 'custom' && (
                      <div className="h-full w-full flex items-center justify-center">
                        <div className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />
                      </div>
                    )}
                  </div>
                  <CalendarIcon className="h-4 w-4 mr-2 opacity-70" />
                  Personalizado...
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        ) : (
          <div className="p-3 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-sm">Selecione o período</h4>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={handleCustomCancel}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <Calendar
              mode="range"
              selected={{ from: customDraft.from, to: customDraft.to }}
              onSelect={(range) =>
                setCustomDraft({ from: range?.from, to: range?.to })
              }
              numberOfMonths={1}
              locale={ptBR}
              className={cn('rounded-md border pointer-events-auto')}
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={handleCustomCancel}>
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={handleCustomConfirm}
                disabled={!customDraft.from || !customDraft.to}
              >
                Aplicar
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  ) : (
    calendarIconInner
  );

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-3 py-1.5 bg-muted/30 border border-border/40 rounded-xl',
        className,
      )}
    >
      {/* Ícone decorativo/gatilho de calendário */}
      {calendarIconNode}

      {/* Seta esquerda - mês anterior (oculta quando preset não-mensal ativo) */}
      {!nonMonthPresetActive && (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={handlePrev}
          title="Mês anterior"
          className="h-7 w-7 rounded-lg transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      )}

      {/* Label do período (mês ou preset) */}
      <span className="text-sm font-semibold tracking-tight text-foreground min-w-[120px] text-center select-none">
        {centerLabel}
      </span>

      {/* Seta direita - próximo mês (oculta quando preset não-mensal ativo) */}
      {!nonMonthPresetActive && (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={handleNext}
          title="Próximo mês"
          className="h-7 w-7 rounded-lg transition-colors"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      )}

      {/* Separador sutil */}
      <div className="w-px h-4 bg-border/60" />

      {/* Botão limpar filtro (limpa mês e preset) */}
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={onClear}
        title="Ver todos os meses"
        className="h-6 w-6 opacity-60 hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  );
}
